import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';

import type { BadgeType } from '../../../types/components/prism/badge.ts';
import type { IconName } from '../../../types/components/prism/icon.ts';
import type { IconBrandName } from '../../../types/components/prism/icon-brand.ts';
import type { IconFlagCode } from '../../../types/components/prism/icon-flag.ts';
import PrismBadge from '../badge.gts';
import PrismIcon from '../icon.gts';
import PrismIconBrand from '../icon-brand.gts';
import PrismIconFlag from '../icon-flag.gts';
import PrismTooltip from '../tooltip.gts';

// A message is required when using the disabled attribute
interface DisabledArgs {
  disabled?: boolean;
  disabledMessage?: string;
}

// You can render a regular icon, a brand icon, or a flag icon individually, but a brand and a flag icon can be rendered together
interface IconsArgs {
  icon?: IconName;
  iconBrand?: IconBrandName;
  iconFlag?: IconFlagCode;
}

interface PrismSelectInputOptionSignature {
  Args: DisabledArgs &
    IconsArgs & {
      badge?: string;
      badgeIcon?: IconName;
      badgeType?: BadgeType;
      label: string;
      selected: boolean;
      // This component is highly dynamic and very hard to type, I haven't found a proper solution for it yet
      // Hence why it is just a generic component that accepts any argument
      /* eslint-disable @typescript-eslint/no-explicit-any */
      unsafe_afterOptionComponent?: ComponentLike<any>;
      unsafe_subLabel?: string;
    };
}

export default class PrismSelectInputOption extends Component<PrismSelectInputOptionSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disabledMessage() {
    return this.args.disabled ? this.args.disabledMessage : undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get iconColor() {
    return this.args.disabled ? 'disabled' : undefined;
  }

  <template>
    {{#if @unsafe_subLabel}}
      <div class="option__unsafe-sub-label-container">
        <p class="text-300--regular option__label truncate">
          {{@label}}
        </p>
        <p class="text-200--regular option__unsafe-sub-label truncate">
          {{@unsafe_subLabel}}
        </p>
      </div>
      {{#if @selected}}
        <PrismIcon @color={{this.iconColor}} @name="checkmark" @size="small" />
      {{/if}}
    {{else}}
      {{#if @icon}}
        <PrismIcon @color={{this.iconColor}} @name={{@icon}} @size="small" />
      {{else}}
        {{#if @iconBrand}}
          <PrismIconBrand @name={{@iconBrand}} />
        {{/if}}
        {{#if @iconFlag}}
          <PrismIconFlag @code={{@iconFlag}} @size="small" />
        {{/if}}
      {{/if}}
      <p class="text-300--regular option__label truncate">{{@label}}</p>
      {{#if @badge}}
        {{#if @badgeIcon}}
          <PrismBadge
            @badgeType={{if @badgeType @badgeType "info"}}
            @iconName={{@badgeIcon}}
            @iconPosition="left"
          >
            {{@badge}}
          </PrismBadge>
        {{else}}
          <PrismBadge @badgeType={{if @badgeType @badgeType "info"}}>
            {{@badge}}
          </PrismBadge>
        {{/if}}
      {{/if}}
      {{#if @unsafe_afterOptionComponent}}
        {{@unsafe_afterOptionComponent}}
      {{/if}}
      {{#if @selected}}
        <PrismIcon @color={{this.iconColor}} @name="checkmark" @size="small" />
      {{/if}}
      {{#if this.disabledMessage}}
        <PrismTooltip @side="right" @text={{this.disabledMessage}} />
      {{/if}}
    {{/if}}
  </template>
}
